import { useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";

import { Option, SearchableSelect as Select } from "./SearchableSelect";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 250] as const;
type PageSize = number | "all";

export function usePaginatedRows<Row>(rows: readonly Row[]) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const pageCount = pageSize === "all" ? 1 : Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(currentPage, pageCount);

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  const visibleRows = pageSize === "all"
    ? rows
    : rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  return {
    pagination: (
      <TablePagination
        currentPage={safePage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(1);
        }}
        pageCount={pageCount}
        pageSize={pageSize}
        totalRecords={rows.length}
      />
    ),
    visibleRows,
  };
}

function TablePagination({
  currentPage,
  onPageChange,
  onPageSizeChange,
  pageCount,
  pageSize,
  totalRecords,
}: {
  currentPage: number;
  onPageChange(page: number): void;
  onPageSizeChange(pageSize: PageSize): void;
  pageCount: number;
  pageSize: PageSize;
  totalRecords: number;
}) {
  const firstRecord = totalRecords === 0 || pageSize === "all"
    ? totalRecords === 0 ? 0 : 1
    : (currentPage - 1) * pageSize + 1;
  const lastRecord = pageSize === "all"
    ? totalRecords
    : Math.min(currentPage * pageSize, totalRecords);

  return (
    <nav aria-label="Table pagination" className="table-pagination">
      <span className="table-pagination__summary">
        {firstRecord}-{lastRecord} of {totalRecords}
      </span>
      <Select
        id={`table-page-size-${usePaginationId()}`}
        label="Rows per page"
        onChange={(event) => onPageSizeChange(event.target.value === "all" ? "all" : Number(event.target.value))}
        value={String(pageSize)}
      >
        {PAGE_SIZE_OPTIONS.map((option) => <Option key={option} text={String(option)} value={String(option)} />)}
        <Option text="All" value="all" />
      </Select>
      <span className="table-pagination__page">Page {currentPage} of {pageCount}</span>
      <Button
        aria-label="Previous page"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
        type="button"
        variant="default"
        variantType="tertiary"
      >
        Previous
      </Button>
      <Button
        aria-label="Next page"
        disabled={currentPage >= pageCount}
        onClick={() => onPageChange(currentPage + 1)}
        type="button"
        variant="default"
        variantType="tertiary"
      >
        Next
      </Button>
    </nav>
  );
}

let paginationId = 0;
function usePaginationId(): number {
  const [id] = useState(() => {
    paginationId += 1;
    return paginationId;
  });
  return id;
}
